import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { 
  Dialog, 
  DialogActions, 
  DialogTitle, 
  Button, 
  RadioGroup, 
  FormControlLabel, 
  Radio 
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import LoadingSpinner from '../../../Loading/component';
import Sidebar from '../../../Navigation/Sidebar/Sidebar';
import './WarehouseAssortmentDetailsComponent.css';

// Icons
import filedownload from "../../../../assests/filedownload.svg";
import calandericon from "../../../../assests/calandericon.svg";
import dragdropimage from "../../../../assests/dragdropimage.svg";
import deleteIconAssortment from "../../../../assests/deleteIconAssortment.svg";
import threeDotIcon from "../../../../assests/threeDotIcon.svg";
import menuFilter from "../../../../assests/menuiFilter.svg";
import sloticon from "../../../../assests/sloticon.svg";
import slotdoubleicon from "../../../../assests/slotdoubleicon.svg";

const WarehouseAssortmentDetailsComponent = ({
  assortmentDetails,
  loading,
  activeTabIndex,
  onTabChange,
  hasChanges,
  onSaveChanges,
  onCancelChanges,
  handleEndAssortment,
  setShowDeleteModal,
  showDeleteModal,
  categories,
  selectedCategory,
  onCategoryChange,
  currentItems,
  remainingItems,
  onItemDrop,
  itemsContainerRef,
  autoScrollDirection,
  onDragUpdate,
  handleCheckRule,
  assortmentSlotDetails = [],
}) => {
  const navigate = useNavigate();
  const { assortmentId } = useParams();
  const [showMenu, setShowMenu] = useState(false);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [userRole, setUserRole] = useState('');
  const menuRef = useRef();
  const dragItemRef = useRef(null);
  const tabsContainerRef = useRef(null);
  
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("data"));
    if (userData) {
      setUserRole(userData.details.userRole.role_name);
    }
  }, []);
  
  // Check if user has read-only permissions (for operations other than download)
  const isReadOnlyUser = ['manager', 'city lead'].includes(userRole?.toLowerCase());
  
  // Check if user can download (admin, EST, manager, city lead)
  const canDownload = ['admin', 'est', 'manager', 'city lead'].includes(userRole?.toLowerCase());

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get all tabs including primary and hardware types
  const getAllTabs = () => {
    const tabs = [
      {
        type: 'primary',
        title: 'Primary',
        count: assortmentDetails.priority_wise_items?.length || 0,
        index: 0
      }
    ];

    if (assortmentDetails.hardware_type_items?.length > 0) {
      assortmentDetails.hardware_type_items.forEach((item, index) => {
        tabs.push({
          type: 'hardware',
          title: item.hardware_type,
          count: item.priority_wise_items?.length || 0,
          index: index + 1
        });
      });
    }

    return tabs;
  };

  const tabs = getAllTabs();
  const itemsPerSlide = 4; 
  const totalSlides = Math.ceil(tabs.length / itemsPerSlide);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const getVisibleTabs = () => {
    const startIndex = currentSlide * itemsPerSlide;
    const endIndex = startIndex + itemsPerSlide;
    return tabs.slice(startIndex, endIndex);
  };

  const handleDragStart = (e, item, sourceType) => {
    if (isReadOnlyUser) {
      e.preventDefault();
      return;
    }
    setDraggedItem(item);
    setDragSource(sourceType);
    dragItemRef.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.mv_id);
    e.currentTarget.classList.add('dragging');
  };

  const handleDragOver = (e, index) => {
    if (isReadOnlyUser) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const targetElement = e.currentTarget;
    const boundingRect = targetElement.getBoundingClientRect();
    const offsetY = e.clientY - boundingRect.top;
    const middleThreshold = boundingRect.height / 2;

    targetElement.classList.remove('drag-over-top', 'drag-over-bottom');

    if (offsetY < middleThreshold) {
      targetElement.classList.add('drag-over-top');
    } else {
      targetElement.classList.add('drag-over-bottom');
    }

    if (onDragUpdate) {
      onDragUpdate(e);
    }
  };

  const handleDragLeave = (e) => {
    if (isReadOnlyUser) return;
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over-top', 'drag-over-bottom');
  };

  const handleDrop = (e, targetIndex) => {
    if (isReadOnlyUser) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over-top', 'drag-over-bottom');

    if (draggedItem && dragSource) {
      onItemDrop(draggedItem, targetIndex, dragSource);
    }

    setDraggedItem(null);
    setDragSource(null);
  };

  const handleDragEnd = (e) => {
    if (isReadOnlyUser) return;
    e.currentTarget.classList.remove('dragging');
    setDraggedItem(null);
    setDragSource(null);
  };

  const handleEditAssortment = () => {
    if (isReadOnlyUser) return;
    navigate(`/home/assortment/assortmenteditdetails/${assortmentId}`);
  };

  const handleCloneAssortment = () => {
    if (isReadOnlyUser) return;
    navigate(`/home/assortment/clone/${assortmentId}`);
  };

  const handleDownload = () => {
    if (!canDownload) return;
    const generateCSVContent = () => {
      const {
        id = '',
        reference_id = '',
        reference_name = '',
        start_date = '',
        priority_wise_items = [],
        rule_failures = [],
        hardware_type_items = [],
        created_by_name = '',
        status = '',
        created_date = '',
        updated_by_name = '',
        last_updated_date = ''
      } = assortmentDetails;

      // Updated headers with Offer Price
      const headers = [
        'Assortment ID',
        'Warehouse Name (Warehouse ID)',
        'Assortment Status',
        'Start Date',
        'VMs Linked',
        'Product Rank',
        'SKU ID',
        'Product Name',
        'Brand Name',
        'MRP',
        // 'Offer Price', // Added Offer Price column
        'Variant',
        'Item Category',
        'Rule Details',
        'Created By',
        'Created Date',
        'Last Updated By',
        'Last Updated Date'
      ];

      const mainData = [
        id,
        reference_id ? `${reference_name} ${reference_id}` : '',
        status,
        formatDate(start_date),
        hardware_type_items.length,
        '', '', '', '', '', '', '', '', // Empty for product-specific columns
        created_by_name,
        formatDate(created_date),
        updated_by_name,
        formatDate(last_updated_date)
      ].map(value => `"${value}"`).join(',');

      const productRows = priority_wise_items.map((item, index) => {
        const hasRuleFailure = rule_failures.some(rule =>
          rule.failedItems?.ids?.includes(item.mv_id)
        );

        return [
          '', '', '', '', '',
          index + 1,
          item.mv_id || '',
          item.variant_name || '',
          item.brand_name || '',
          item.mrp || item.offer_price || '', // MRP or fallback to offer price
          // item.offer_price || '', // Offer Price
          item.variant_name || '',
          item.categories?.join(', ') || '',
          hasRuleFailure ? 'No' : 'Yes',
          '', '', '', ''
        ].map(value => `"${value}"`).join(',');
      });

      return [headers.join(','), mainData, ...productRows].join('\r\n');
    };

    const csvContent = generateCSVContent();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Warehouse_Assortment_${assortmentDetails.id || 'details'}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEndAssortmentWithCheck = () => {
    if (isReadOnlyUser) return;
    handleEndAssortment();
  };

  const handleCheckRuleWithCheck = () => {
    if (isReadOnlyUser) return;
    handleCheckRule();
  };

  const handleCategoryChangeWithCheck = (category) => {
    if (isReadOnlyUser) return;
    onCategoryChange(category);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderSlotIcon = (rank) => {
    if (activeTabIndex === 0 || !Array.isArray(assortmentSlotDetails)) return null;

    const matchedSlot = assortmentSlotDetails.find(slot => {
      const expectedRank = slot.slotId % 100;
      return expectedRank === rank;
    });

    if (!matchedSlot) return null;

    return (
      <div className="warehouse-assortment-slot-icon">
        <img
          src={matchedSlot.isMerged ? slotdoubleicon : sloticon}
          alt={matchedSlot.isMerged ? "Merged Slot" : "Single Slot"}
          style={{ width: "24px", height: "24px" }}
        />
      </div>
    );
  };

  const visibleTabs = getVisibleTabs();

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="warehouse-assortment-details-container">
          <Sidebar />
          <div className="warehouse-assortment-content-wrapper">
            <div className="warehouse-assortment-header-container">
              <div className="warehouse-assortment-header-left-section">
                <button
                  className="warehouse-assortment-back-button"
                  onClick={() => navigate(-1)}
                >
                  ←
                </button>
                <div className="warehouse-assortment-title-section">
                  <h1 className="warehouse-assortment-title">
                    Assortment Id: <strong>{assortmentDetails.id || 'N/A'}</strong>
                  </h1>
                  <p className="warehouse-assortment-created-by">
                    Created By: {assortmentDetails.created_by_name} {assortmentDetails.created_by || 'N/A'}
                  </p>
                </div>
              </div>
              <div className="warehouse-assortment-header-actions">
                {!isReadOnlyUser && assortmentDetails.status === "active" && (
                  <>
                    <button onClick={handleCloneAssortment} className="warehouse-assortment-clone-button">
                      Clone
                    </button>
                    {canDownload && (
                      <button className="warehouse-assortment-download-button" onClick={handleDownload}>
                        <img src={filedownload} alt="Download" />
                      </button>
                    )}
                  </>
                )}

                {!isReadOnlyUser && assortmentDetails.status === "scheduled" && (
                  <>
                    {activeTabIndex === 0 ? (
                      <>
                        <button className="warehouse-assortment-filter-button">
                          <img
                            src={menuFilter}
                            alt="Filter"
                            onClick={() => setShowFilterPopup(true)}
                            title="Filter by category"
                          />
                        </button>
                        <button className="warehouse-assortment-clone-button" onClick={handleEditAssortment}>
                          Edit
                        </button>
                        <Dialog
                          open={showFilterPopup}
                          onClose={() => setShowFilterPopup(false)}
                          className="filter-dialog"
                          PaperProps={{ className: "filter-popup-paper" }}
                        >
                          <DialogTitle className="filter-popup-title">Select Category</DialogTitle>
                          <RadioGroup
                            value={selectedCategory}
                            onChange={(e) => {
                              handleCategoryChangeWithCheck(e.target.value);
                              setShowFilterPopup(false);
                            }}
                          >
                            {categories.map((category) => (
                              <FormControlLabel
                                key={category}
                                value={category}
                                control={<Radio />}
                                label={category}
                                className="filter-radio-label"
                              />
                            ))}
                          </RadioGroup>
                        </Dialog>
                        <div className="menu-wrapper" ref={menuRef}>
                          {canDownload && (
                            <button
                              className="warehouse-assortment-download-button"
                              onClick={() => setShowMenu(!showMenu)}
                              title="More options"
                            >
                              <img src={threeDotIcon} alt="More" />
                            </button>
                          )}
                          {showMenu && (
                            <div className="menu-popup">
                              <div className="menu-item" onClick={handleDownload}>
                                <img src={filedownload} alt="Download" className="menu-icon" />
                                <span>Download</span>
                              </div>
                              <div className="menu-item" onClick={() => setShowDeleteModal(true)}>
                                <img src={deleteIconAssortment} alt="Delete" className="menu-icon" />
                                <span>Delete</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      canDownload && (
                        <button className="warehouse-assortment-download-button" onClick={handleDownload}>
                          <img src={filedownload} alt="Download" />
                        </button>
                      )
                    )}
                  </>
                )}

                {!isReadOnlyUser && assortmentDetails.status === "inactive" && (
                  <>
                    <button className="warehouse-assortment-clone-button" onClick={handleCloneAssortment}>
                      Clone
                    </button>
                    {canDownload && (
                      <button className="warehouse-assortment-download-button" onClick={handleDownload}>
                        <img src={filedownload} alt="Download" />
                      </button>
                    )}
                  </>
                )}

                {/* Show download button for read-only users (manager, city lead) */}
                {isReadOnlyUser && canDownload && (
                  <>
                    <button className="warehouse-assortment-download-button" onClick={handleDownload}>
                      <img src={filedownload} alt="Download" />
                    </button>
                    <div className="warehouse-assortment-read-only-badge">
                      View Only
                    </div>
                  </>
                )}

                {/* Show badge for users who can't download or perform operations */}
                {!canDownload && (
                  <div className="warehouse-assortment-read-only-badge">
                    View Only
                  </div>
                )}
              </div>
            </div>
            
            {!isReadOnlyUser && (
              <Dialog
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                className="custom-delete-dialog"
              >
                <DialogTitle className="custom-delete-dialog-title">
                  Are you sure you want to Delete This Assortment Id: <strong>{assortmentDetails.id}</strong>?
                </DialogTitle>
                <DialogActions className="custom-delete-dialog-actions">
                  <Button onClick={() => setShowDeleteModal(false)} variant="outlined">
                    No
                  </Button>
                  <Button onClick={handleEndAssortmentWithCheck} variant="contained" color="error">
                    Yes
                  </Button>
                </DialogActions>
              </Dialog>
            )}

            <div className="warehouse-assortment-main-content">
              <div className="warehouse-assortment-left-section">
                <div className="warehouse-assortment-info-section">
                  <div className="warehouse-assortment-date-status">
                    <img
                      src={calandericon}
                      alt="Calendar"
                      className="warehouse-assortment-calendar-icon"
                    />
                    <div className="warehouse-assortment-date-info">
                      <span className="warehouse-assortment-date-label">Started On</span>
                      <div className="warehouse-assortment-date-value">
                        {formatDate(assortmentDetails.start_date)}
                      </div>
                    </div>
                    <span className={`warehouse-assortment-status ${assortmentDetails.status?.toLowerCase()}`}>
                      {assortmentDetails.status}
                    </span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
                    {(assortmentDetails.hardware_type_items?.length > 0 || assortmentDetails.priority_wise_items?.length > 0) && (
                      <div className="warehouse-assortment-hardware-tabs-container">
                        <div className="warehouse-assortment-hardware-tabs-wrapper">
                          {totalSlides > 1 && (
                            <button
                              className="carousel-button carousel-prev"
                              onClick={prevSlide}
                              disabled={currentSlide === 0}
                            >
                              <ChevronLeft />
                            </button>
                          )}

                          <div className="warehouse-assortment-hardware-tabs" ref={tabsContainerRef}>
                            {visibleTabs.map((tab) => (
                              <div
                                key={tab.index}
                                className={`warehouse-assortment-hardware-tab ${activeTabIndex === tab.index ? "active" : ""}`}
                                onClick={() => onTabChange(tab.index)}
                              >
                                <div className="warehouse-assortment-tab-title">
                                  {tab.title}
                                </div>
                                <div className="warehouse-assortment-tab-count">
                                  {tab.count} Items
                                </div>
                              </div>
                            ))}
                          </div>

                          {totalSlides > 1 && (
                            <button
                              className="carousel-button carousel-next"
                              onClick={nextSlide}
                              disabled={currentSlide === totalSlides - 1}
                            >
                              <ChevronRight />
                            </button>
                          )}
                        </div>

                        {totalSlides > 1 && (
                          <div className="carousel-indicators">
                            {Array.from({ length: totalSlides }).map((_, index) => (
                              <button
                                key={index}
                                className={`carousel-indicator ${currentSlide === index ? 'active' : ''}`}
                                onClick={() => setCurrentSlide(index)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {assortmentDetails.rule_failures?.length > 0 && (
                      <div className="warehouse-assortment-rule-failures">
                        <div style={{ display: "flex", flexDirection: "column", margin: "0px" }}>
                          <h2 className="warehouse-assortment-rule-failures-title">
                            {assortmentDetails.rule_failures.length} Rule Fail <br />
                          </h2>
                          <span>
                            {assortmentDetails.rule_failures[0]?.failureType}
                          </span>
                        </div>
                        <div style={{ width: "120px", height: "70px", fontSize: "14px" }}>
                          {!isReadOnlyUser && (
                            <button
                              onClick={handleCheckRuleWithCheck}
                              style={{
                                border: "1px solid  #14AE78",
                                backgroundColor: "transparent",
                                color: "#14AE78",
                                padding: "8px",
                                borderRadius: "12px"
                              }}
                            >
                              Check Again
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="warehouse-assortment-items-wrapper">
                  <div
                    className={`warehouse-assortment-items-container ${autoScrollDirection ? `auto-scroll-${autoScrollDirection}` : ''} ${isReadOnlyUser ? 'read-only' : ''}`}
                    ref={itemsContainerRef}
                  >
                    <div className="warehouse-assortment-primary-section">
                      <div className="warehouse-assortment-section-header">
                        <h3>
                          {activeTabIndex === 0 ? 'Primary Assortment' : `${tabs[activeTabIndex]?.title} Assortment`}
                          ({currentItems.length} items)
                        </h3>
                      </div>
                      <div className="warehouse-assortment-items-grid">
                        {currentItems.map((item, index) => (
                          <div
                            key={`primary-${item.mv_id}-${index}`}
                            className={`warehouse-assortment-item-card ${item.hasRankChanged ? 'rank-changed' : ''} ${isReadOnlyUser ? 'read-only-item' : ''}`}
                            draggable={!isReadOnlyUser}
                            onDragStart={(e) => handleDragStart(e, item, 'primary')}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                          >
                            <div className="warehouse-assortment-item-index">
                              {item.rank < 10 ? `0${item.rank}` : item.rank}
                            </div>

                            {renderSlotIcon(item.rank)}

                            <div className="warehouse-assortment-item-content-wrapper">
                              <div className="warehouse-assortment-item-content">
                                <div className="warehouse-assortment-item-image">
                                  {item.image ? (
                                    <img
                                      src={`https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/${item.image}`}
                                      alt={item.variant_name}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.display = "none";
                                        e.target.parentNode.innerHTML = "<span>No Image</span>";
                                      }}
                                    />
                                  ) : (
                                    <span>No Image</span>
                                  )}
                                </div>
                                <div className="warehouse-assortment-item-details">
                                  <div className="warehouse-assortment-item-name">
                                    {item.variant_name}
                                  </div>
                                  <div className="warehouse-assortment-item-meta">
                                    <span className="warehouse-assortment-item-mvid">
                                      MVID: {item.mv_id}
                                    </span>
                                    <span className="warehouse-assortment-item-price">
                                      MRP: ₹{item.mrp || item.offer_price || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {item.hasRankChanged && item.originalRank && (
                                <div className="warehouse-assortment-rank-change">
                                  <span className="rank-change-text">
                                    Moved from rank {item.originalRank} ➜ {item.rank}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {remainingItems.length > 0 && (
                      <div className="warehouse-assortment-secondary-section">
                        <div className="warehouse-assortment-section-header">
                          <h3>Secondary Assortment ({remainingItems.length} items)</h3>
                          {!isReadOnlyUser && <p>Drag items to add them to the primary assortment</p>}
                        </div>
                        <div className="warehouse-assortment-items-grid secondary-items-grid">
                          {remainingItems.map((item, index) => (
                            <div
                              key={`secondary-${item.mv_id}-${index}`}
                              className={`warehouse-assortment-item-card secondary-item ${item.hasRankChanged ? 'rank-changed' : ''} ${isReadOnlyUser ? 'read-only-item' : ''}`}
                              draggable={!isReadOnlyUser}
                              onDragStart={(e) => handleDragStart(e, item, 'secondary')}
                              onDragOver={(e) => handleDragOver(e, index)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, index)}
                              onDragEnd={handleDragEnd}
                            >
                              <div className="warehouse-assortment-item-index">
                                {item.rank < 10 ? `0${item.rank}` : item.rank}
                              </div>

                              <div className="warehouse-assortment-item-content-wrapper">
                                <div className="warehouse-assortment-item-content">
                                  <div className="warehouse-assortment-item-image">
                                    {item.image ? (
                                      <img
                                        src={`https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/${item.image}`}
                                        alt={item.variant_name}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.style.display = "none";
                                          e.target.parentNode.innerHTML = "<span>No Image</span>";
                                        }}
                                      />
                                    ) : (
                                      <span>No Image</span>
                                    )}
                                  </div>
                                  <div className="warehouse-assortment-item-details">
                                    <div className="warehouse-assortment-item-name">
                                      {item.variant_name}
                                    </div>
                                    <div className="warehouse-assortment-item-meta">
                                      <span className="warehouse-assortment-item-mvid">
                                        MVID: {item.mv_id}
                                      </span>
                                      <span className="warehouse-assortment-item-price">
                                        MRP: ₹{item.mrp || item.offer_price || 'N/A'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {item.hasRankChanged && item.originalRank && (
                                  <div className="warehouse-assortment-rank-change">
                                    <span className="rank-change-text">
                                      Moved from rank {item.originalRank} ➜ {item.rank}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {!isReadOnlyUser && hasChanges && (
                  <div className="warehouse-assortment-save-changes-bar">
                    <div className="warehouse-assortment-save-message">
                      <img
                        src={dragdropimage}
                        alt="Drag and drop"
                        className="warehouse-assortment-drag-icon"
                      />
                      Note: To change items priority you can drag & drop items card from the list.
                    </div>
                    <div className="warehouse-assortment-save-actions">
                      <button
                        className="warehouse-assortment-cancel-button"
                        onClick={onCancelChanges}
                      >
                        Cancel
                      </button>
                      <button
                        className="warehouse-assortment-save-button"
                        onClick={onSaveChanges}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WarehouseAssortmentDetailsComponent;